// Dev-only preview fixtures: sample data for viewing a single renderer in
// isolation via ?preview=<type>, with no study, session, or backend. Each
// fixture supplies the step the renderer receives, plus any canned server
// content the renderer would normally fetch (consent/debrief form HTML, a chat
// reply). Used by PreviewHarness + installPreviewFetch. NOT shipped to prod:
// the entry point only references these under import.meta.env.DEV, so the
// production build dead-code-eliminates them.

import type {
  StepEnvelope,
  FormResponse,
  SurveyContent,
  SurveyQuestion,
  DocumentContent,
} from "../api/types";

export type PreviewScriptStep =
  | { type: "normal"; response: string }
  | { type: "ab_test"; response_a: string; response_b: string };

export interface PreviewFixture {
  label: string;
  step: StepEnvelope;
  form?: FormResponse; // served for GET /sessions/:id/form (consent, debrief)
  survey?: SurveyContent; // served for GET /sessions/:id/survey (survey)
  document?: DocumentContent; // served for GET /sessions/:id/document (document)
  chatReply?: string; // canned assistant reply for POST /sessions/:id/chat (block)
  chatReplyDelayMs?: number; // hold the reply this long (to watch the typing indicator)
  chatChunkDelayMs?: number; // if set, stream the reply line by line at this interval
  chatSurvey?: { name: string; questions: SurveyQuestion[] }; // GET /chat/survey (inter-trial)
  abOptionA?: string; // POST /chat/ab Option A candidate
  abOptionB?: string; // POST /chat/ab Option B candidate (content / ui_alt)
  script?: PreviewScriptStep[]; // SCRIPTED_RESPONSES: the canned step sequence
  scriptedDelayMs?: number; // scripted reply / dialog reveal delay
  chatHistory?: { role: "user" | "assistant"; content: string }[]; // GET /chat/history seed
  sttText?: string; // canned transcript returned by the preview STT route
}

export const PREVIEW_FIXTURES: Record<string, PreviewFixture> = {
  "block-ab-content": {
    label: "Chat: A/B content test",
    step: {
      type: "block",
      id: "preview-ab-content",
      name: "Chat",
      counterbalance: false,
      features: { AB_TESTING: true },
      settings: {
        ab_test_message_threshold: 2,
        ab_dialog_message: "Which reply do you prefer?",
        ab_kind_tone_b: true,
      },
    },
    abOptionA: "That does sound frustrating. Tell me a bit more about what happened?",
    abOptionB: "I hear you, and I'm really sorry you're dealing with that. Let's take it one step at a time together.",
  },
  "block-ab-ui-test": {
    label: "Chat: A/B UI test",
    step: {
      type: "block",
      id: "preview-ab-ui",
      name: "Chat",
      counterbalance: false,
      features: { AB_UI_TEST: true, TYPEWRITER: true },
      settings: {
        ab_test_message_threshold: 2,
        ab_dialog_message: "Which presentation do you prefer?",
        typewriter_speed_ms: 18,
        ab_typewriter_b: false,
      },
    },
    abOptionA: "Here is the same answer, shown two different ways so you can compare how it feels.",
  },
  "block-ab-ui-alt": {
    label: "Chat: A/B reword test",
    step: {
      type: "block",
      id: "preview-ab-alt",
      name: "Chat",
      counterbalance: false,
      features: { AB_UI_ALT: true },
      settings: {
        ab_test_message_threshold: 2,
        ab_dialog_message: "Which wording reads better?",
      },
    },
    abOptionA: "You should try breaking the task into smaller steps and tackling them one by one.",
    abOptionB: "Consider splitting the work into a few smaller pieces, then handling each piece in turn.",
  },
  "block-voice": {
    label: "Chat: voice (speak + record)",
    step: {
      type: "block",
      id: "preview-voice",
      name: "Chat",
      counterbalance: false,
      features: { VOICE_CONVERSATION: true },
      settings: {
        voice_id: "alloy",
        voice_model: "tts-1",
        voice_display_mode: "voice_and_text",
        voice_stt_enabled: true,
      },
    },
    chatReply: "Here is a spoken reply. A short tone stands in for the voice audio.",
    sttText: "This is what the microphone heard (simulated).",
  },
  "block-voice-only": {
    label: "Chat: voice only (no text)",
    step: {
      type: "block",
      id: "preview-voice-only",
      name: "Chat",
      counterbalance: false,
      features: { VOICE_CONVERSATION: true },
      settings: {
        voice_id: "alloy",
        voice_model: "tts-1",
        voice_display_mode: "voice_only",
        voice_stt_enabled: true,
      },
    },
    chatReply: "You should hear this rather than read it; the bubble shows an audio indicator.",
    sttText: "Voice-only transcription (simulated).",
  },
  "block-carryover": {
    label: "Chat: carried-over history (clear-on-start off)",
    step: {
      type: "block",
      id: "preview-carryover",
      name: "Chat",
      counterbalance: false,
      features: {},
      settings: {},
    },
    chatHistory: [
      { role: "user", content: "This message is from an earlier chat block." },
      { role: "assistant", content: "And so is this reply. The screen was seeded on entry." },
    ],
    chatReply: "New reply in this block, continuing the same conversation.",
  },
  "block-scripted": {
    label: "Chat: scripted responses",
    step: {
      type: "block",
      id: "preview-scripted",
      name: "Chat",
      counterbalance: false,
      features: { SCRIPTED_RESPONSES: true, STREAMING: true, TYPING_INDICATOR: true },
      settings: {
        ab_dialog_message: "Which reply do you prefer?",
        scripted_response_delay_s: 1,
      },
    },
    script: [
      {
        type: "normal",
        response:
          "Welcome! This reply is scripted, not generated by a model.\nSend another message to continue the script.",
      },
      {
        type: "ab_test",
        response_a: "Option A: the upbeat, encouraging version of this scripted reply.",
        response_b: "Option B: the calm, measured version of this scripted reply.",
      },
      {
        type: "normal",
        response: "Thanks for choosing. This is the next scripted line in the sequence.",
      },
    ],
    scriptedDelayMs: 800,
    chatReply: "The script has ended, so from here a real model would take over.",
    chatChunkDelayMs: 40,
  },
  transition: {
    label: "Transition",
    step: {
      type: "transition",
      id: "preview-transition",
      name: "Transition",
      counterbalance: false,
      settings: {
        end_condition: "timed",
        duration_seconds: 30,
        show_countdown: true,
        show_animated_text: true,
        animated_text: "Loading",
        font_size: 26,
        background_color: "#2c3e50",
        pause_after_seconds: 0,
        text_content: {
          high_center: "High Center Text",
          center_center: "Center Center Text",
          main_box: "Main Box Text",
          low_center: "Low Center Text",
        },
      },
    },
  },

  // Same transition with a LIGHT background, to show the text auto-flipping to
  // black (the renderer's contrast rule). Reached via ?preview=transition-light.
  "transition-light": {
    label: "Transition (light bg)",
    step: {
      type: "transition",
      id: "preview-transition-light",
      name: "Transition",
      counterbalance: false,
      settings: {
        end_condition: "timed",
        duration_seconds: 30,
        show_countdown: true,
        show_animated_text: true,
        animated_text: "Loading",
        font_size: 26,
        background_color: "#f5f0e1", // light cream -> text should render black
        pause_after_seconds: 0,
        text_content: {
          high_center: "High Center Text",
          center_center: "Center Center Text",
          main_box: "Main Box Text",
          low_center: "Low Center Text",
        },
      },
    },
  },

  writing: {
    label: "Writing",
    step: {
      type: "writing",
      id: "preview-writing",
      name: "Writing",
      counterbalance: false,
      settings: {
        title: "Title",
        prompt_text: "This is the prompt text",
        placeholder: "Start writing here...",
        count_by: "words",
        min_count: 50,
        max_count: 500,
        end_condition: "submit",
        pause_after_seconds: 0,
      },
    },
  },

  survey: {
    label: "Survey",
    step: {
      type: "survey",
      id: "preview-survey",
      name: "Survey",
      counterbalance: false,
      settings: { survey_id: "preview-sv", pause_after_seconds: 0 },
    },
    survey: {
      type: "survey",
      survey_id: "preview-sv",
      name: "RA Test",
      questions: [
        { id: "h1", type: "heading", text: "This is a header" },
        {
          id: "p1",
          type: "percentage_scale",
          question: "Percentage question",
          scale: ["Left Label", "Right Label"],
        },
        {
          id: "l1",
          type: "likert",
          question: "Likert Question",
          points: 5,
          anchors: { "1": "Strongly Disagree", "5": "Strongly Agree" },
        },
        {
          id: "m1",
          type: "multiple_choice",
          question: "Multiple Choice Question",
          options: ["Option 1", "Option 2"],
        },
        {
          id: "c1",
          type: "checkbox",
          question: "Checkbox Question",
          options: ["Choice A", "Choice B"],
        },
        { id: "i1", type: "input", question: "Input Question", placeholder: "" },
      ],
    },
  },

  input_gate: {
    label: "Input gate",
    step: {
      type: "input_gate",
      id: "preview-gate",
      name: "Input gate",
      counterbalance: false,
      settings: {
        expected_input: "OPEN",
        prompt_text: "Enter the code to continue:",
        background_color: "#2d3748",
        pause_after_seconds: 0,
      },
    },
  },

  document: {
    label: "Document",
    step: {
      type: "document",
      id: "preview-document",
      name: "Document",
      counterbalance: false,
      settings: {
        title: "This is the Title",
        instructions: "This is the Instruction",
        document_media_id: "preview-doc",
        document_filename: "Company Key.pdf",
        document_extension: "pdf",
        end_condition: "both",
        duration_seconds: 600,
        min_read_seconds: 5,
        show_countdown: true,
        pause_after_seconds: 0,
      },
    },
    document: {
      type: "document",
      document_media_id: "preview-doc",
      document_filename: "Company Key.pdf",
      document_extension: "pdf",
    },
  },

  // Typed instruction screen. min_read_seconds holds the button for 3s so the
  // hold-then-release is visible in the preview.
  instruction: {
    label: "Instruction",
    step: {
      type: "instruction",
      id: "preview-instruction",
      name: "Instruction",
      counterbalance: false,
      settings: {
        title: "Before you begin",
        body_text:
          "In the next part of the study you will chat with an **AI assistant** about a short task.\n\n" +
          "Please keep the following in mind:\n\n" +
          "- Answer in your *own words*; there are no right or wrong answers.\n" +
          "- Stay on this tab until the section ends.\n" +
          "- The section takes about 5 minutes.\n\n" +
          "| Key | What it does |\n| --- | --- |\n| Enter | Sends your message |\n| Shift + Enter | Starts a new line |\n\n" +
          "When you are ready, click the button below.",
        button_label: "I understand",
        min_read_seconds: 3,
        pause_after_seconds: 0,
      },
    },
  },

  // Real Eriksen flanker definition (matches the desktop template structure),
  // supplied inline the way the engine reads it. Practice block (4 trials, with
  // feedback) then a short test block (8 trials, no feedback).
  task: {
    label: "Task (flanker)",
    step: {
      type: "task",
      id: "preview-task",
      name: "Flanker",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "Flanker",
          task_type: "flanker",
          instructions:
            "ERIKSEN FLANKER TASK\n\nYou will see a row of arrows on each trial.\n\nIdentify the direction of the CENTRE arrow, ignoring the flankers.\n\nPress:   F = Left      J = Right\n\nRespond as quickly and accurately as possible.",
          settings: { background_color: "#000000" },
          response_keys: { f: "left", j: "right" },
          trial_template: [
            { type: "fixation", duration_ms: 500 },
            { type: "stimulus", await_response: true, timeout_ms: 1500 },
            { type: "feedback", duration_ms: 800 },
            { type: "iti", duration_ms: 200 },
          ],
          stimuli: [
            { type: "text", text: "\u2190  \u2190  \u2190  \u2190  \u2190", color: "#FFFFFF", font_size: 72, condition: "congruent", correct_response: "left" },
            { type: "text", text: "\u2192  \u2192  \u2192  \u2192  \u2192", color: "#FFFFFF", font_size: 72, condition: "congruent", correct_response: "right" },
            { type: "text", text: "\u2192  \u2192  \u2190  \u2192  \u2192", color: "#FFFFFF", font_size: 72, condition: "incongruent", correct_response: "left" },
            { type: "text", text: "\u2190  \u2190  \u2192  \u2190  \u2190", color: "#FFFFFF", font_size: 72, condition: "incongruent", correct_response: "right" },
          ],
          blocks: [
            {
              label: "practice",
              num_trials: 4,
              randomize: true,
              show_feedback: true,
              feedback_duration_ms: 800,
              instructions: "PRACTICE BLOCK\n\nYou will get feedback after each response.\n\nF = Left      J = Right",
            },
            { label: "test", num_trials: 8, randomize: true, show_feedback: false },
          ],
        },
      },
    },
  },

  // Stroop: name the INK color, ignore the word. Keys d/f/j -> red/green/blue.
  stroop: {
    label: "Task (stroop)",
    step: {
      type: "task",
      id: "preview-stroop",
      name: "Stroop",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "Stroop",
          task_type: "stroop",
          instructions:
            "STROOP TASK\n\nName the INK COLOR of each word, not the word itself.\n\nD = Red      F = Green      J = Blue\n\nRespond as quickly and accurately as possible.",
          settings: { background_color: "#808080" },
          response_keys: { d: "red", f: "green", j: "blue" },
          trial_template: [
            { type: "fixation", duration_ms: 500 },
            { type: "stimulus", await_response: true, timeout_ms: 3000 },
            { type: "feedback", duration_ms: 800 },
            { type: "iti", duration_ms: 200 },
          ],
          stimuli: [
            { type: "text", text: "RED", color: "#FF0000", condition: "congruent", correct_response: "red" },
            { type: "text", text: "GREEN", color: "#00CC00", condition: "congruent", correct_response: "green" },
            { type: "text", text: "BLUE", color: "#4444FF", condition: "congruent", correct_response: "blue" },
            { type: "text", text: "RED", color: "#00CC00", condition: "incongruent", correct_response: "green" },
            { type: "text", text: "GREEN", color: "#4444FF", condition: "incongruent", correct_response: "blue" },
            { type: "text", text: "BLUE", color: "#FF0000", condition: "incongruent", correct_response: "red" },
          ],
          blocks: [
            { label: "practice", num_trials: 4, randomize: true, show_feedback: true, feedback_duration_ms: 800, instructions: "PRACTICE BLOCK\n\nName the INK COLOR.\n\nD = Red    F = Green    J = Blue" },
            { label: "test", num_trials: 6, randomize: true, show_feedback: false },
          ],
        },
      },
    },
  },

  // Lexical decision: is it a real word? Keys f/j -> word/nonword.
  ldt: {
    label: "Task (lexical decision)",
    step: {
      type: "task",
      id: "preview-ldt",
      name: "Lexical Decision",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "Lexical Decision",
          task_type: "lexical_decision",
          instructions:
            "LEXICAL DECISION TASK\n\nDecide whether each letter string is a real word.\n\nF = Real word      J = Nonword\n\nRespond as quickly and accurately as possible.",
          settings: { background_color: "#000000" },
          response_keys: { f: "word", j: "nonword" },
          trial_template: [
            { type: "fixation", duration_ms: 500 },
            { type: "stimulus", await_response: true, timeout_ms: 3000 },
            { type: "feedback", duration_ms: 600 },
            { type: "iti", duration_ms: 200 },
          ],
          stimuli: [
            { type: "text", text: "TABLE", color: "#FFFFFF", font_size: 64, condition: "word", correct_response: "word" },
            { type: "text", text: "HOUSE", color: "#FFFFFF", font_size: 64, condition: "word", correct_response: "word" },
            { type: "text", text: "WATER", color: "#FFFFFF", font_size: 64, condition: "word", correct_response: "word" },
            { type: "text", text: "FLIRP", color: "#FFFFFF", font_size: 64, condition: "nonword", correct_response: "nonword" },
            { type: "text", text: "TRALK", color: "#FFFFFF", font_size: 64, condition: "nonword", correct_response: "nonword" },
            { type: "text", text: "SNOMP", color: "#FFFFFF", font_size: 64, condition: "nonword", correct_response: "nonword" },
          ],
          blocks: [
            { label: "practice", num_trials: 4, randomize: true, show_feedback: true, instructions: "PRACTICE BLOCK\n\nF = Real word\nJ = Nonword" },
            { label: "test", num_trials: 6, randomize: true, show_feedback: false },
          ],
        },
      },
    },
  },

  // SART: press SPACE for every digit EXCEPT the target (3). Tests the space key,
  // go/no-go scoring, and per-trial font-size variation.
  sart: {
    label: "Task (SART)",
    step: {
      type: "task",
      id: "preview-sart",
      name: "SART",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "SART (Sustained Attention)",
          task_type: "sart",
          instructions:
            "SART (SUSTAINED ATTENTION)\n\nPress SPACE for every digit EXCEPT 3.\n\nWhen you see 3, do NOT press anything.\n\nRespond as quickly and accurately as possible.",
          settings: {
            background_color: "#000000",
            fb_correct_text: "Correct",
            fb_correct_color: "#30D158",
            fb_incorrect_text: "Incorrect",
            fb_incorrect_color: "#FF453A",
            target_digit: 3,
            font_variation: true,
            font_sizes: [18, 29, 34, 40, 48],
          },
          response_keys: { space: "go" },
          trial_template: [
            { type: "fixation", duration_ms: 300, color: "#FFFFFF" },
            { type: "stimulus", await_response: true, timeout_ms: 1150 },
            { type: "feedback", duration_ms: 0 },
            { type: "iti", duration_ms: 200 },
          ],
          stimuli: [1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => ({
            type: "text",
            text: String(d),
            color: "#FFFFFF",
            condition: d === 3 ? "nogo" : "go",
            correct_response: d === 3 ? "" : "go",
            font_size: 48,
            font_size_variation: [18, 29, 34, 40, 48],
          })),
          blocks: [
            { label: "practice", num_trials: 6, randomize: true, show_feedback: true, feedback_duration_ms: 0, instructions: "PRACTICE BLOCK\n\nPress SPACE for every digit except 3." },
            { label: "test", num_trials: 10, randomize: true, show_feedback: false },
          ],
        },
      },
    },
  },

  // N-back: press SPACE when the letter matches the one 2 back. Tests the gap
  // response window (await on a timed stimulus AND a timed iti) and RT measured
  // from stimulus onset across phases.
  nback: {
    label: "Task (n-back)",
    step: {
      type: "task",
      id: "preview-nback",
      name: "N-Back",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "N-Back Task",
          task_type: "nback",
          instructions:
            "N-BACK TASK\n\nLetters appear one at a time.\n\nPress SPACE if the current letter is the SAME as the one shown 2 steps ago.\n\nOtherwise, do NOT press anything.",
          settings: { background_color: "#000000", n_level: 2, stim_type: "letters" },
          response_keys: { space: "match" },
          trial_template: [
            { type: "fixation", duration_ms: 300, color: "#FFFFFF" },
            { type: "stimulus", await_response: true, duration_ms: 500 },
            { type: "iti", duration_ms: 2500, await_response: true },
            { type: "feedback", duration_ms: 800 },
          ],
          stimuli: [],
          blocks: [
            {
              label: "practice",
              num_trials: 6,
              pre_sequenced: true,
              randomize: false,
              show_feedback: true,
              instructions: "PRACTICE BLOCK\n\nSPACE = match (same as 2 back)\nNo key = different",
              stimuli: [
                { type: "text", text: "F", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "G", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "F", color: "#FFFFFF", font_size: 72, condition: "match", correct_response: "match" },
                { type: "text", text: "H", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "G", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "F", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
              ],
            },
            {
              label: "test",
              num_trials: 8,
              pre_sequenced: true,
              randomize: false,
              show_feedback: false,
              break_after_trials: 30,
              stimuli: [
                { type: "text", text: "F", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "K", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "F", color: "#FFFFFF", font_size: 72, condition: "match", correct_response: "match" },
                { type: "text", text: "K", color: "#FFFFFF", font_size: 72, condition: "match", correct_response: "match" },
                { type: "text", text: "M", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "F", color: "#FFFFFF", font_size: 72, condition: "no_response", correct_response: "no_response" },
                { type: "text", text: "M", color: "#FFFFFF", font_size: 72, condition: "match", correct_response: "match" },
                { type: "text", text: "F", color: "#FFFFFF", font_size: 72, condition: "match", correct_response: "match" },
              ],
            },
          ],
        },
      },
    },
  },

  // Task switching: a colored cue frame tells you which rule to apply to the
  // digit (magnitude vs parity). Exercises the cue phase + cue-overlay stimulus.
  task_switching: {
    label: "Task (task switching)",
    step: {
      type: "task",
      id: "preview-taskswitch",
      name: "Task Switching",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "Task-Switching (Magnitude/Parity)",
          task_type: "task_switching",
          instructions:
            "TASK-SWITCHING\n\nA colored frame tells you which rule to use.\n\nBlue (Magnitude): F = Low (<5), J = High (>5)\nOrange (Parity): F = Odd, J = Even\n\nRespond as quickly and accurately as possible.",
          settings: {
            background_color: "#000000",
            task_a_label: "Magnitude",
            task_b_label: "Parity",
            task_a_cue_color: "#60a5fa",
            task_b_cue_color: "#fb923c",
            cue_type: "border",
            cue_border_width: 6,
            key_left: "f",
            key_right: "j",
            csi_ms: 600,
          },
          response_keys: { f: "left", j: "right" },
          trial_template: [
            { type: "fixation", duration_ms: 500, color: "#FFFFFF" },
            { type: "cue", duration_ms: 600 },
            { type: "stimulus", await_response: true, timeout_ms: 3000 },
            { type: "feedback", duration_ms: 500 },
            { type: "iti", duration_ms: 200 },
          ],
          stimuli: [],
          blocks: [
            {
              label: "practice_a",
              num_trials: 3,
              randomize: true,
              show_feedback: true,
              feedback_duration_ms: 500,
              instructions: "PRACTICE: MAGNITUDE\n\nBlue frame: F = Low (<5), J = High (>5)",
              stimuli: [
                { type: "text", text: "1", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "pure", correct_response: "left" },
                { type: "text", text: "2", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "pure", correct_response: "left" },
                { type: "text", text: "7", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "pure", correct_response: "right" },
                { type: "text", text: "8", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "pure", correct_response: "right" },
              ],
            },
            {
              label: "mixed",
              num_trials: 6,
              pre_sequenced: true,
              randomize: false,
              show_feedback: false,
              break_after_trials: 32,
              instructions: "MIXED BLOCK\n\nWatch the frame color for the rule.\n\nBlue (Magnitude): F = Low, J = High\nOrange (Parity): F = Odd, J = Even",
              stimuli: [
                { type: "text", text: "7", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "first", correct_response: "right" },
                { type: "text", text: "2", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "repeat", correct_response: "left" },
                { type: "text", text: "7", color: "#FFFFFF", font_size: 72, task: "b", cue_color: "#fb923c", cue_text: "Parity", condition: "switch", correct_response: "left" },
                { type: "text", text: "8", color: "#FFFFFF", font_size: 72, task: "b", cue_color: "#fb923c", cue_text: "Parity", condition: "repeat", correct_response: "right" },
                { type: "text", text: "1", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "switch", correct_response: "left" },
                { type: "text", text: "8", color: "#FFFFFF", font_size: 72, task: "a", cue_color: "#60a5fa", cue_text: "Magnitude", condition: "repeat", correct_response: "right" },
              ],
            },
          ],
        },
      },
    },
  },

  // PVT: react as fast as possible to a running ms counter after a random delay.
  // Exercises the animated counter stimulus and the rt_feedback phase. Delays
  // are shortened here so the preview is quick.
  pvt: {
    label: "Task (PVT)",
    step: {
      type: "task",
      id: "preview-pvt",
      name: "PVT",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "Psychomotor Vigilance Task",
          task_type: "pvt",
          instructions:
            "PSYCHOMOTOR VIGILANCE TASK\n\nA counter appears after a random delay.\n\nPress SPACE as FAST as you can when it appears.\n\nStay alert, the delay varies each time.",
          settings: {
            background_color: "#000000",
            font_size: 72,
            response_key: "space",
            stimulus_mode: "counter",
            stimulus_color: "#FF0000",
            lapse_threshold_ms: 500,
            show_rt_feedback: true,
            feedback_duration_ms: 1000,
            timeout_ms: 30000,
            rt_normal_color: "#00CC00",
            rt_lapse_color: "#FF4444",
            rt_timeout_color: "#FFAA00",
          },
          response_keys: { space: "respond" },
          trial_template: [
            { type: "iti", duration_ms: null, await_response: false },
            { type: "stimulus", duration_ms: null, timeout_ms: 30000, await_response: true },
            { type: "rt_feedback", duration_ms: 1000, await_response: false },
          ],
          stimuli: [],
          blocks: [
            {
              label: "practice",
              num_trials: 2,
              pre_sequenced: true,
              randomize: false,
              show_feedback: false,
              instructions: "PRACTICE\n\nPress SPACE as soon as the counter appears.",
              stimuli: [
                { type: "counter", color: "#FF0000", font_size: 72, condition: "vigilance", correct_response: "respond", iti_duration_ms: 900 },
                { type: "counter", color: "#FF0000", font_size: 72, condition: "vigilance", correct_response: "respond", iti_duration_ms: 1400 },
              ],
            },
            {
              label: "test",
              num_trials: 3,
              pre_sequenced: true,
              randomize: false,
              show_feedback: false,
              stimuli: [
                { type: "counter", color: "#FF0000", font_size: 72, condition: "vigilance", correct_response: "respond", iti_duration_ms: 1100 },
                { type: "counter", color: "#FF0000", font_size: 72, condition: "vigilance", correct_response: "respond", iti_duration_ms: 1800 },
                { type: "counter", color: "#FF0000", font_size: 72, condition: "vigilance", correct_response: "respond", iti_duration_ms: 1000 },
              ],
            },
          ],
        },
      },
    },
  },

  // MRT: tap SPACE in time with the metronome, then keep the rhythm when it
  // stops (continuation). This preview uses "both" mode (flash + tone) to
  // exercise the visual and audio beat engines; the default template is visual.
  mrt: {
    label: "Task (MRT beat)",
    step: {
      type: "task",
      id: "preview-mrt",
      name: "MRT",
      counterbalance: false,
      settings: {
        task_definition: {
          task_name: "Metronome Response Task",
          task_type: "mrt",
          instructions:
            "METRONOME RESPONSE TASK\n\nA metronome beats at a steady rhythm.\n\nPress SPACE in time with each beat, not before, not after.\n\nWhen the metronome stops, keep tapping at the same rhythm.",
          settings: {
            background_color: "#000000",
            metronome_mode: "both",
            pre_beat_ms: 288,
            stim_duration_ms: 75,
            post_beat_ms: 287,
            flash_color: "#FFFFFF",
            font_size: 72,
            response_key: "space",
            beat_sound_source: "default",
            use_default_tone: true,
          },
          response_keys: { space: "tap" },
          trial_template: [
            { type: "iti", duration_ms: 288, await_response: true },
            { type: "stimulus", duration_ms: 75, await_response: true },
            { type: "iti", duration_ms: 287, await_response: true },
          ],
          stimuli: [],
          blocks: [
            {
              label: "practice",
              num_trials: 4,
              pre_sequenced: true,
              randomize: false,
              show_feedback: false,
              instructions: "PRACTICE\n\nTap SPACE in time with the beat.",
              stimuli: Array.from({ length: 4 }, () => ({
                type: "text",
                text: "\u25cf",
                color: "#FFFFFF",
                font_size: 72,
                condition: "synchronization",
                correct_response: "tap",
                play_sound: true,
              })),
            },
            {
              label: "test",
              num_trials: 6,
              pre_sequenced: true,
              randomize: false,
              show_feedback: false,
              stimuli: Array.from({ length: 6 }, () => ({
                type: "text",
                text: "\u25cf",
                color: "#FFFFFF",
                font_size: 72,
                condition: "synchronization",
                correct_response: "tap",
                play_sound: true,
              })),
            },
            {
              label: "continuation",
              num_trials: 4,
              pre_sequenced: true,
              randomize: false,
              show_feedback: false,
              instructions: "CONTINUATION\n\nThe metronome will stop.\nKeep tapping at the same rhythm.",
              stimuli: Array.from({ length: 4 }, () => ({
                type: "text",
                text: "",
                color: "#000000",
                font_size: 72,
                condition: "continuation",
                correct_response: "tap",
                play_sound: false,
              })),
            },
          ],
        },
      },
    },
  },

  consent: {
    label: "Consent",
    step: {
      type: "consent",
      id: "preview-consent",
      name: "Consent",
      counterbalance: false,
      settings: { form_id: "preview-form" },
    },
    form: {
      type: "consent",
      form_id: "preview-form",
      content:
        "<h3>Sample Consent</h3><p>This is sample consent text shown only in the preview harness.</p><p>Do you agree to take part?</p>",
    },
  },

  debrief: {
    label: "Debrief",
    step: {
      type: "debrief",
      id: "preview-debrief",
      name: "Debrief",
      counterbalance: false,
      settings: { form_id: "preview-form" },
    },
    form: {
      type: "debrief",
      form_id: "preview-form",
      content:
        "<h3>Sample Debrief</h3><p>Thank you for taking part. This is sample debrief text shown only in the preview harness.</p>",
    },
  },

  block: {
    label: "Chat (block)",
    step: {
      type: "block",
      id: "preview-chat",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 5,
        show_start_message: true,
        start_message_text: "The next section is about to begin.",
        start_message_font_size: 18,
        start_message_color: "#e8590c",
      },
      // No features -> typing indicator off -> reply appears instantly (default).
    },
    chatReply:
      "Hello! Here is a quick example of formatting:\n\n" +
      "- **bold** and *italic* text\n" +
      "- a second list item\n\n" +
      "```python\nprint(\"hello world\")\n```\n\n" +
      "That covers the basics.",
  },

  "block-indicator": {
    label: "Chat (typing indicator)",
    step: {
      type: "block",
      id: "preview-chat-indicator",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 5,
        show_start_message: true,
        start_message_text: "The next section is about to begin.",
        start_message_font_size: 18,
        start_message_color: "#e8590c",
        typing_indicator_style: "wave_dots",
        typing_indicator_label: "Thinking",
      },
      features: { TYPING_INDICATOR: true },
    },
    chatReplyDelayMs: 4000, // hold the reply so the indicator animates
    chatReply: "Thanks for waiting! The indicator animated while I was thinking.",
  },

  "block-features": {
    label: "Chat (text size + custom title + scroll lock + min duration)",
    step: {
      type: "block",
      id: "preview-chat-features",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text: "Feature parity preview.",
        start_message_font_size: 18,
        start_message_color: "#e8590c",
        text_size: 60,
        custom_chat_title: "JOLIVE Study Bot",
        scroll_lock: true,
        typing_indicator_style: "dots",
        typing_indicator_label: "Thinking",
        typing_indicator_min_duration_s: 2,
      },
      features: { TEXT_SIZE_CHANGER: true, CUSTOM_CHAT_TITLE: true, TYPING_INDICATOR: true },
    },
    chatReplyDelayMs: 200, // reply is fast; the indicator still holds ~2s (min duration)
    chatReply:
      "This reply is large because the text size changer is on. Send several messages " +
      "to watch scroll lock keep you pinned to the bottom. Check the browser tab title too.",
  },

  "block-streaming": {
    label: "Chat (streaming, line by line)",
    step: {
      type: "block",
      id: "preview-chat-streaming",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text: "Streaming preview.",
        start_message_font_size: 18,
        start_message_color: "#e8590c",
        typing_indicator_style: "dots",
      },
      features: { STREAMING: true, TYPING_INDICATOR: true },
    },
    chatChunkDelayMs: 350, // stream the reply one line at a time
    chatReply:
      "Here is a streamed reply that arrives line by line.\n\n" +
      "- first point\n- second point\n- third point\n\n" +
      "And a short code block once the fence closes:\n\n" +
      "```python\nfor i in range(3):\n    print(i)\n```\n\n" +
      "That is the end of the streamed reply.",
  },

  "block-typewriter": {
    label: "Chat (typewriter)",
    step: {
      type: "block",
      id: "preview-chat-typewriter",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text: "Typewriter preview.",
        start_message_font_size: 18,
        start_message_color: "#e8590c",
        typewriter_speed_ms: 25,
      },
      features: { TYPEWRITER: true },
    },
    chatReply:
      "Watch this type out. Plain text appears character by character, while " +
      "**bold text**, `inline code`, and lists render instantly:\n\n" +
      "- first point\n- second point\n\n" +
      "And a code block appears as it forms:\n\n" +
      "```python\nprint(\"hello\")\n```\n\n" +
      "Done.",
  },

  "block-delay-before-send": {
    label: "Chat (delay before send)",
    step: {
      type: "block",
      id: "preview-chat-delay",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text:
          "Delay-before-send preview: your message posts at once, then there is a 2s pause before anything happens.",
        start_message_font_size: 16,
        start_message_color: "#1971c2",
        delay_seconds: 2,
        typing_indicator_label: "Thinking",
      },
      features: { DELAY_BEFORE_SEND: true, TYPING_INDICATOR: true },
    },
    chatReply: "Here is the reply, which only started after the send delay elapsed.",
  },

  "block-slowdown": {
    label: "Chat (slowdown)",
    step: {
      type: "block",
      id: "preview-chat-slowdown",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text:
          "Slowdown preview (forced always-on): every reply is held back by at least 4 seconds.",
        start_message_font_size: 16,
        start_message_color: "#e8590c",
        slowdown_permanent_after_enabled: true,
        slowdown_permanent_after_s: 0,
        slowdown_min_delay_s: 4,
        typing_indicator_label: "Thinking",
      },
      features: { SLOWDOWN: true, TYPING_INDICATOR: true },
    },
    chatReply: "This reply was held back by the slowdown window before it appeared.",
  },

  "block-block-messages": {
    label: "Chat (block messages)",
    step: {
      type: "block",
      id: "preview-chat-block",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text:
          "Block-messages preview: after every 2nd reply the input locks for 5 seconds.",
        start_message_font_size: 16,
        start_message_color: "#2f9e44",
        block_message_count: 2,
        block_duration_s: 5,
        block_repeat: true,
      },
      features: { BLOCK_MSGS: true },
    },
    chatReply: "Reply received. Keep sending; the input will lock after the 2nd reply.",
  },

  "block-erase-history": {
    label: "Chat (erase history)",
    step: {
      type: "block",
      id: "preview-chat-erase",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text:
          "Erase-history preview: send a few messages; about 6s in, the transcript clears and the AI forgets it.",
        start_message_font_size: 16,
        start_message_color: "#9c36b5",
        erase_history_delay_s: 6,
        erase_history_repeat: false,
      },
      features: { ERASE_HISTORY: true },
    },
    chatReply: "Reply received. Watch this clear when the erase timer fires.",
  },

  "block-web-search": {
    label: "Chat (web search)",
    step: {
      type: "block",
      id: "preview-chat-websearch",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        show_start_message: true,
        start_message_text:
          "Web-search preview: type a query and click Search Web to see a grounded reply with sources.",
        start_message_font_size: 16,
        start_message_color: "#1971c2",
      },
      features: { WEB_SEARCH: true },
    },
    chatReply:
      "Area code 321 serves Brevard and Seminole counties in Florida's Space Coast region.",
  },

  "block-token-numeric": {
    label: "Chat (token budget: numeric)",
    step: {
      type: "block",
      id: "preview-chat-token-numeric",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        token_budget_amount: 1000,
        token_cost_rate: 4,
        token_display_type: "numeric_label",
      },
      features: { TOKEN_BUDGET: true },
    },
    chatReply: "Sure! Here is a reply that costs some tokens to send.",
  },

  "block-token-bar": {
    label: "Chat (token budget: progress bar)",
    step: {
      type: "block",
      id: "preview-chat-token-bar",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        token_budget_amount: 1000,
        token_cost_rate: 4,
        token_display_type: "progress_bar",
        token_pb_show_numbers: true,
        token_pb_color_shift: true,
      },
      features: { TOKEN_BUDGET: true },
    },
    chatReply: "Sure! Here is a reply that costs some tokens to send.",
  },

  "block-token-ring": {
    label: "Chat (token budget: fuel gauge)",
    step: {
      type: "block",
      id: "preview-chat-token-ring",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        token_budget_amount: 1000,
        token_cost_rate: 4,
        token_display_type: "fuel_gauge",
        token_fg_show_numbers: true,
        token_fg_show_percentage: false,
      },
      features: { TOKEN_BUDGET: true },
    },
    chatReply: "Sure! Here is a reply that costs some tokens to send.",
  },

  "block-token-coins": {
    label: "Chat (token budget: coins)",
    step: {
      type: "block",
      id: "preview-chat-token-coins",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        token_budget_amount: 500,
        token_cost_rate: 4,
        token_display_type: "coins",
        token_coins_per_coin: 100,
      },
      features: { TOKEN_BUDGET: true },
    },
    chatReply: "Sure! Here is a reply that costs some tokens to send.",
  },

  "block-token-battery": {
    label: "Chat (token budget: battery)",
    step: {
      type: "block",
      id: "preview-chat-token-battery",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        token_budget_amount: 1000,
        token_cost_rate: 4,
        token_display_type: "battery",
        token_bat_segments: 4,
        token_bat_show_numbers: false,
        token_bat_flash_critical: true,
      },
      features: { TOKEN_BUDGET: true },
    },
    chatReply: "Sure! Here is a reply that costs some tokens to send.",
  },

  "block-token-warnings": {
    label: "Chat (token budget: text warnings)",
    step: {
      type: "block",
      id: "preview-chat-token-warnings",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        // Small budget so a few messages cross the 50/25/10 thresholds.
        token_budget_amount: 60,
        token_cost_rate: 4,
        token_display_type: "text_warnings",
        token_warnings: [
          { threshold: 50, message: "⚠️ You have used half of your tokens." },
          { threshold: 25, message: "⚠️ Warning: 25% of tokens remaining." },
          { threshold: 10, message: "🚨 Critical: Only 10% of tokens remaining!" },
        ],
      },
      features: { TOKEN_BUDGET: true },
    },
    chatReply: "Sure! Here is a reply that costs some tokens to send.",
  },

  "block-inter-trial-survey": {
    label: "Chat (inter-trial survey every 2 replies)",
    step: {
      type: "block",
      id: "preview-chat-its",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        survey_id: "svy-preview",
        survey_trigger_count: 2,
        survey_delay_before_show_seconds: 0,
        inter_trial_scroll_lock: false,
      },
      features: { INTER_TRIAL_SURVEY: true },
    },
    chatReply: "Got it, thanks for sharing.",
    chatSurvey: {
      name: "Quick Check",
      questions: [
        { id: "h1", type: "heading", text: "Quick check-in" },
        {
          id: "q1",
          type: "likert",
          question: "How natural did that feel?",
          points: 5,
          anchors: { "1": "Not at all", "5": "Very" },
        },
        { id: "q2", type: "input", question: "Anything to add?", placeholder: "optional thoughts" },
      ],
    },
  },

  "block-timed-survey": {
    label: "Chat (timed survey, once, 5 seconds in)",
    step: {
      type: "block",
      id: "preview-chat-timed-survey",
      name: "Chat",
      counterbalance: false,
      settings: {
        end_condition: "messages",
        auto_end_messages: 50,
        mid_block_surveys: [
          { id: "t1", survey_id: "svy-preview", trigger_kind: "time", value: 5, pause: true },
        ],
      },
      features: {},
    },
    chatReply: "Got it, thanks for sharing.",
    chatSurvey: {
      name: "Timed Check",
      questions: [
        { id: "q1", type: "input", question: "How is the chat going so far?", placeholder: "a few words" },
      ],
    },
  },
};
